import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Home, Phone } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { trackQRScan } from '../services/qrTrackingService';
import { v4 as uuidv4 } from 'uuid';

interface DeliveryOptionProps {
  onSelect: (option: 'surplace' | 'livraison') => void;
}

const DeliveryOption: React.FC<DeliveryOptionProps> = ({ onSelect }) => {
  const [selectedButton, setSelectedButton] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setTableInfo } = useCart();
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Never fire the delayed onSelect (or setState) after unmount.
  useEffect(() => {
    return () => {
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tableNumber = params.get('table');
    const tableName = params.get('name');
    
    if (tableNumber && tableName) {
      trackQRScan({
        tableNumber,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
        sessionId: uuidv4()
      });
      
      setTableInfo(tableNumber, tableName);
      navigate('/menu', { replace: true });
    }
  }, [location, setTableInfo, navigate]);

  const handleSelection = async (option: 'surplace' | 'livraison', immediate = false) => {
    setSelectedButton(option);
    setIsAnimating(true);
    
    localStorage.setItem('deliveryOption', option);
    
    const delay = immediate ? 0 : 500;
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
    }
    transitionTimer.current = setTimeout(() => {
      onSelect(option);
    }, delay);
  };

  const handleCall = () => {
    // Highlight only — do NOT set isAnimating here: the tel: navigation does
    // not leave the page on desktop, and the fade-out left the whole screen
    // invisible with every button disabled.
    setSelectedButton('call');
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
    }
    transitionTimer.current = setTimeout(() => setSelectedButton(null), 800);
    window.location.href = `tel:+212632302829`;
  };

  return (
    <div className={`min-h-screen flex items-center justify-center 
      bg-gradient-to-br from-[#FBBF24] via-[#F59E0B] to-[#EA580C]
      px-3 sm:px-6 md:px-8 lg:px-12 py-4 sm:py-8 md:py-12
      transition-opacity duration-500 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
      <div className="w-full max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
        <div className="mb-4 sm:mb-8 md:mb-12 text-center">
          <img 
            src={`${process.env.PUBLIC_URL}/locales/img/djoco3.png`} 
            alt="DJOCO" 
            className="h-24 sm:h-32 md:h-40 mx-auto mb-4 drop-shadow-xl"
          />
        </div>
        
        <div className="space-y-3 sm:space-y-6 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:space-y-0">
          <button
            onClick={() => handleSelection('surplace')}
            disabled={isAnimating}
            className={`w-full bg-white/95 backdrop-blur rounded-3xl shadow-xl shadow-orange-900/10 hover:shadow-2xl hover:-translate-y-1
              transition-all duration-300 overflow-hidden group
              ${selectedButton === 'surplace' ? 'scale-95 opacity-80' : ''}
              ${isAnimating ? 'pointer-events-none' : ''}
              h-full`}
          >
            <div className="py-5 sm:py-7 md:py-9 px-3 sm:px-4 md:px-6 flex flex-col items-center justify-center">
              <div className={`w-14 sm:w-16 md:w-20 h-14 sm:h-16 md:h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100
                flex items-center justify-center mb-3 sm:mb-4 md:mb-5
                transition-transform duration-300 
                ${selectedButton === 'surplace' ? 'scale-110' : 'group-hover:scale-110'}`}>
                <MapPin className="w-6 sm:w-8 md:w-10 h-6 sm:h-8 md:h-10 text-[#EA580C]" />
              </div>
              <div className="text-center">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-800 mb-1 sm:mb-2">
                  Sur place
                </h2>
                <p className="text-xs sm:text-sm text-stone-500">
                  Commander et manger au restaurant
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleSelection('livraison')}
            disabled={isAnimating}
            className={`w-full bg-white/95 backdrop-blur rounded-3xl shadow-xl shadow-orange-900/10 hover:shadow-2xl hover:-translate-y-1
              transition-all duration-300 overflow-hidden group
              ${selectedButton === 'livraison' ? 'scale-95 opacity-80' : ''}
              ${isAnimating ? 'pointer-events-none' : ''}
              h-full`}
          >
            <div className="py-5 sm:py-7 md:py-9 px-3 sm:px-4 md:px-6 flex flex-col items-center justify-center">
              <div className={`w-14 sm:w-16 md:w-20 h-14 sm:h-16 md:h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100
                flex items-center justify-center mb-3 sm:mb-4 md:mb-5
                transition-transform duration-300
                ${selectedButton === 'livraison' ? 'scale-110' : 'group-hover:scale-110'}`}>
                <Home className="w-6 sm:w-8 md:w-10 h-6 sm:h-8 md:h-10 text-[#EA580C]" />
              </div>
              <div className="text-center">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-800 mb-1 sm:mb-2">
                  Livraison
                </h2>
                <p className="text-xs sm:text-sm text-stone-500">
                  Se faire livrer à domicile
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={handleCall}
            disabled={isAnimating}
            className={`w-full bg-white/95 backdrop-blur rounded-3xl shadow-xl shadow-orange-900/10 hover:shadow-2xl hover:-translate-y-1
              transition-all duration-300 overflow-hidden group
              ${selectedButton === 'call' ? 'scale-95 opacity-80' : ''}
              ${isAnimating ? 'pointer-events-none' : ''}
              h-full`}
          >
            <div className="py-5 sm:py-7 md:py-9 px-3 sm:px-4 md:px-6 flex flex-col items-center justify-center">
              <div className={`w-14 sm:w-16 md:w-20 h-14 sm:h-16 md:h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100
                flex items-center justify-center mb-3 sm:mb-4 md:mb-5
                transition-transform duration-300
                ${selectedButton === 'call' ? 'scale-110' : 'group-hover:scale-110'}`}>
                <Phone className="w-6 sm:w-8 md:w-10 h-6 sm:h-8 md:h-10 text-[#EA580C]" />
              </div>
              <div className="text-center">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-800 mb-1 sm:mb-2">
                  Allo DJOCO
                </h2>
                <p className="text-xs sm:text-sm text-stone-500">
                  +212 6 32 30 28 29
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOption;